import {
  addToast,
  CircularSpinnerß,
  Dialogß,
  ErrorToast,
} from '@otter-ai/den-components';
import { createCheckoutSession, type Plan } from '@otter-ai/recall-client';
import {
  checkAndLogStatSigFeatureGate,
  PurchaseCheckoutSessionCreateEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { useAtomValue } from 'jotai';
import { type ReactNode, useCallback, useState } from 'react';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { checkoutTrackingAtom } from '@/shared/modals/cc-trial-modal/utils/atoms';
import { getMinSeats } from '@/shared/utils/workspaceUtils';
import { CheckoutDialogForm } from './CheckoutDialogForm';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise() {
  if (!stripePromise) {
    stripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY as string, {
        betas: ['custom_checkout_server_updates_1'],
      }),
    );
  }
  return stripePromise;
}

type CheckoutDialogProps = {
  done: () => void;
  title: ReactNode;
  initialPlan: Plan;
  planOptions: {
    annualPlan?: Plan;
    monthlyPlan?: Plan;
  };
  submitButtonText: string;
  testId?: string;
  checkoutType: 'reactivate' | 'upgrade';
  purchaseInProgress: boolean;
  setPurchaseInProgress: (purchaseInProgress: boolean) => void;
  isEdu?: boolean;
};

export function CheckoutDialog({
  done,
  title,
  initialPlan,
  planOptions,
  submitButtonText,
  checkoutType,
  testId = 'checkout-dialog',
  purchaseInProgress,
  setPurchaseInProgress,
  isEdu = false,
}: CheckoutDialogProps) {
  const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>(
    initialPlan,
  );

  const [sessionIsUpdating, setSessionIsUpdating] = useState(false);
  const [isCheckoutProviderReady, setIsCheckoutProviderReady] = useState(false);
  const checkoutTracking = useAtomValue(checkoutTrackingAtom);

  const { workspace, isLoading: isLoadingWorkspace } = useWorkspace();

  const handleRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setIsCheckoutProviderReady(true);
    } else {
      setIsCheckoutProviderReady(false);
    }
  }, []);

  const numWorkspaceMembers = getMinSeats({
    isEdu,
    workspace,
    selectedPlan,
  });

  if (!initialPlan || isLoadingWorkspace) {
    return null;
  }

  return (
    <Dialogß
      onOpenChange={(open) => {
        if (!open) done();
      }}
      data-testid={testId}
      title={title}
      action={null}
      className="max-w-[612px]"
    >
      {!isCheckoutProviderReady && (
        <div className="flex justify-center items-center py-8">
          <CircularSpinnerß data-testid="checkout-spinner" />
        </div>
      )}

      <CheckoutProvider
        stripe={getStripePromise()}
        options={{
          fetchClientSecret: async () => {
            const subscriptionMetaChange = await checkAndLogStatSigFeatureGate(
              'subscription_meta_change',
            );

            const response = await createCheckoutSession({
              body: {
                session_params: {
                  line_items: [
                    {
                      price: initialPlan.stripe_plan_id || '',
                      quantity: numWorkspaceMembers,
                    },
                  ],
                  ...(subscriptionMetaChange && {
                    subscription_data: {
                      metadata: {
                        source: 'otter_server',
                      },
                    },
                  }),
                },
              },
            });
            if (response.data) {
              trackEvent(
                PurchaseCheckoutSessionCreateEvent({
                  Status: 'success',
                  CheckoutType: checkoutType,
                  ...(checkoutTracking.premiumFeature && {
                    PremiumFeature: checkoutTracking.premiumFeature,
                  }),
                  ...(checkoutTracking.checkoutEntryPoint && {
                    CheckoutEntryPoint: checkoutTracking.checkoutEntryPoint,
                  }),
                }),
              );
              return Promise.resolve(
                response.data?.checkout_session_client_secret || '',
              );
            }
            trackEvent(
              PurchaseCheckoutSessionCreateEvent({
                Status: 'failure',
                ErrorMessage: response.error?.message,
                ErrorCode: response.error?.code,
                CheckoutType: checkoutType,
                ...(checkoutTracking.premiumFeature && {
                  PremiumFeature: checkoutTracking.premiumFeature,
                }),
                ...(checkoutTracking.checkoutEntryPoint && {
                  CheckoutEntryPoint: checkoutTracking.checkoutEntryPoint,
                }),
              }),
            );
            const errorMessage =
              response.error?.message || 'Unable to process purchase';
            addToast(<ErrorToast description={errorMessage} />);
            done();
            return Promise.reject(new Error(errorMessage));
          },
        }}
      >
        <CheckoutDialogForm
          ref={handleRef}
          onCloseDialog={done}
          selectedPlan={selectedPlan}
          planOptions={planOptions}
          setSelectedPlan={setSelectedPlan}
          numWorkspaceMembers={numWorkspaceMembers}
          sessionIsUpdating={sessionIsUpdating}
          setSessionIsUpdating={setSessionIsUpdating}
          submitButtonText={submitButtonText}
          checkoutType={checkoutType}
          purchaseInProgress={purchaseInProgress}
          setPurchaseInProgress={setPurchaseInProgress}
        />
      </CheckoutProvider>
    </Dialogß>
  );
}
